import React, { useMemo, useRef } from 'react';
import { Flex, useDisclosure, useToast } from '@withjoy/joykit';
import { Pencil, Trash, WarningTriangle, Close, Eye } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResponsiveMenu, ResponsiveMenuAction } from '@shared/components/ResponsiveMenu';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { ACCOMMODATIONS_ACTIONS } from '@website-editor/customComponents/Accommodations/Accommodations.constants';
import { useAdminAccommodationsTelemetry } from '@website-editor/customComponents/Accommodations/Accommodations.telemetry';
import { AccommodationCard, TravelMapBlockType } from '@website-editor/customComponents/Accommodations/Accommodations.types';
import { useAccommodationsContext } from '@website-editor/customComponents/Accommodations/context/useAccommodationsContext';
import { useTranslation } from '@shared/core';
import { useCancelRoomBlock } from '../../hooks/useCancelRoomBlock';
import { CheckIcon } from '@assets/icons';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useEventInfo } from '@shared/utils/eventInfo';
import { AccommodationPlaceV2 } from '../../pages/Hotel/HotelForm.types';
import { useHotelRedirectUrl } from '@apps/admin/routes/Planning/routes/Accommodations/hooks/useHotelRedirectUrl';
import { addError } from '@shared/utils/logger';
import { RoomBlockCancelDialog } from '../RoomBlockCancelDialog';
import { formatNowForTelemetry } from '@shared/utils/telemetryDateFormatter';

interface ActionMenuProps {
  hotel: AccommodationCard;
}

export const ActionMenu = ({ hotel }: ActionMenuProps) => {
  const { isOpen, onToggle } = useDisclosure();
  const portalContainerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { t } = useTranslation('websiteEditor');
  const travelCustomPage = t('customPage');
  const hotelTrans = t('customPage', 'travelMap', 'hotel');
  const cancelRoomBlockTrans = t('customPage', 'cancelRoomBlock');

  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { navigateToScreen } = useNavigationPaneContext();
  const { setCustomComponentBlockData, setShowDeletePopup, setRefetchQueries, setSelectedHotelPhotos } = useCustomComponentStateContext();
  const telemetry = useAdminAccommodationsTelemetry();
  const { eventHandle } = useEventInfo();
  const { accommodationsData, accommodationPropertyPhotos, isSuperAdmin } = useAccommodationsContext();
  const { isOpen: isOpenRoomBlockCancelDialog, onClose: onCloseRoomBlockCancelDialog, onOpen: onOpenRoomBlockCancelDialog } = useDisclosure();
  const hotelUrl = useHotelRedirectUrl({
    hotel: { ...hotel, type: hotel.type },
    searchParams: {
      skipAvailabilityCheck: 'true',
      utm_medium: 'web',
      utm_source: 'admin_accommodations',
      utm_campaign: 'hotel_list_action_menu',
    },
  });
  const isJoyManagedRoomBlock = hotel.isJoyManagedRoomBlock;

  const onCancelError = useEventCallback(() => toast(cancelRoomBlockTrans.submitError(), { icon: <WarningTriangle /> }));

  const handleCancelRoomBlock = useEventCallback(() => {
    const currentDateTime = formatNowForTelemetry();
    telemetry.cancelRoomBlock('accommodations', {
      roomBlockId: hotel.id ?? '',
      isSuperAdmin: true,
      website: eventHandle || '',
      datetime: currentDateTime,
    });
    onOpenRoomBlockCancelDialog();
  }, [isOpenRoomBlockCancelDialog, telemetry, hotel.id, eventHandle]);

  const onCancelled = () => {
    updateWebsitePreview();
    setCustomComponentBlockData({});
    setRefetchQueries(['AccommodationHotels']);
    toast(cancelRoomBlockTrans.successMessage(), { icon: <CheckIcon /> });
    onCloseRoomBlockCancelDialog();
  };

  const { canceling, onCancelRoomBlock } = useCancelRoomBlock({
    pageSlug: 'accommodations',
    eventHandle: eventHandle ?? '',
    isSuperAdmin: false,
    displayName: hotel.displayName ?? '',
    onCancelled,
    onCancelError,
    accommodationsData,
  });

  const hideDelete = !isSuperAdmin && isJoyManagedRoomBlock;

  const handleEditHotel = useEventCallback(() => {
    const accommodationPhotos = accommodationPropertyPhotos?.searchAccommodationProperties?.properties?.find(place => place.joyPlaceId === hotel.joyPlaceId);

    switch (hotel.type) {
      case TravelMapBlockType.travelMapAccommodationsRoomblock: {
        const roomBlockToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!roomBlockToEdit) return;
        telemetry.editItemClicked('accommodations.RoomBlock', { itemType: hotel.type, isSuperAdmin: !!isSuperAdmin });

        setCustomComponentBlockData(roomBlockToEdit);
        setSelectedHotelPhotos(accommodationPhotos?.photos ?? []);
        if ((roomBlockToEdit as AccommodationPlaceV2).type === 'custom') {
          navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_EDIT);
        } else {
          navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_HOTEL_EDIT);
        }
        break;
      }
      case TravelMapBlockType.travelMapAccommodationsHotel: {
        const hotelToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!hotelToEdit) return;

        setCustomComponentBlockData(hotelToEdit);
        setSelectedHotelPhotos(accommodationPhotos?.photos ?? []);
        navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_HOTEL_EDIT);
        break;
      }
      case TravelMapBlockType.travelMapAccommodationsCustom: {
        const customPropToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!customPropToEdit) return;

        setCustomComponentBlockData(customPropToEdit);
        navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_EDIT);
        break;
      }
    }
  });

  const handleDeleteHotel = useEventCallback(() => {
    switch (hotel.type) {
      case TravelMapBlockType.travelMapAccommodationsRoomblock: {
        const roomBlockToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!roomBlockToEdit) {
          addError('Unable to find room block error');
          return;
        }
        telemetry.deleteAccommodationPlaceDialog('accommodations.RoomBlock', { itemType: hotel.type, isSuperAdmin: !!isSuperAdmin, createdByType: hotel?.createdByType });
        setCustomComponentBlockData({ ...roomBlockToEdit, type: TravelMapBlockType.travelMapAccommodationsRoomblock });
        setShowDeletePopup(true);
        break;
      }
      case TravelMapBlockType.travelMapAccommodationsHotel: {
        const hotelToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!hotelToEdit) {
          addError('Unable to find hotel error');
          return;
        }
        telemetry.deleteAccommodationPlaceDialog('accommodations.AccommodationPlaces', { itemType: hotel.type, isSuperAdmin: !!isSuperAdmin, createdByType: hotel?.createdByType });
        setCustomComponentBlockData({ ...hotelToEdit, type: TravelMapBlockType.travelMapAccommodationsHotel });
        setShowDeletePopup(true);
        break;
      }
      case TravelMapBlockType.travelMapAccommodationsCustom: {
        const customPropToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!customPropToEdit) {
          addError('Unable to find custom property error');
          return;
        }
        telemetry.deleteAccommodationPlaceDialog('accommodations.AccommodationPlaces', {
          itemType: hotel.type,
          isSuperAdmin: !!isSuperAdmin,
          createdByType: customPropToEdit?.createdByType,
        });
        setCustomComponentBlockData({ ...customPropToEdit, type: TravelMapBlockType.travelMapAccommodationsCustom });
        setShowDeletePopup(true);
        break;
      }
    }
  });

  const actions: ResponsiveMenuAction[] = useMemo(() => {
    const items: ResponsiveMenuAction[] = [
      {
        title: hotelTrans.editStay(),
        icon: Pencil,
        onClick: handleEditHotel,
      },
    ];

    if (hotelUrl) {
      items.push({
        title: hotelTrans.explore(),
        icon: Eye,
        url: hotelUrl,
      });
    }

    if (isJoyManagedRoomBlock) {
      items.push({
        title: cancelRoomBlockTrans.cancelOption(),
        icon: Close,
        onClick: handleCancelRoomBlock,
      });
    }

    if (!hideDelete) {
      items.push({
        title: hotel.type === TravelMapBlockType.travelMapAccommodationsRoomblock ? travelCustomPage.delete.travelMapAccommodationsRoomblock?.deleteCta() : hotelTrans.remove(),
        icon: Trash,
        onClick: handleDeleteHotel,
      });
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDeleteHotel, handleEditHotel, hideDelete, hotelUrl]);

  return (
    <Flex
      ref={portalContainerRef}
      width={pxToRem(40)}
      minWidth={pxToRem(40)}
      height={pxToRem(40)}
      borderRadius="50%"
      overflow="hidden"
      alignItems="center"
      justifyContent="center"
      border="1px solid"
      borderColor="mono3"
    >
      <ResponsiveMenu
        iconSize="md"
        isOpen={isOpen}
        onToggle={onToggle}
        actions={actions}
        lastOptionNeutralStyle
        menuWidth={pxToRem(226)}
        horizontalIcon
        usePortal
        portalContainer={portalContainerRef.current ?? undefined}
        itemListProps={{ borderRadius: pxToRem(12), padding: pxToRem(16) }}
        customDefaultPosition={{ x: -4.5, y: -1 }}
      />

      <RoomBlockCancelDialog
        loading={canceling}
        isOpen={isOpenRoomBlockCancelDialog}
        onClose={onCloseRoomBlockCancelDialog}
        onCancelRoomBlock={onCancelRoomBlock}
        id={hotel?.id || ''}
      />
    </Flex>
  );
};
